/**
 * SyncProgress — visual progress for an in-flight Bookinglayer sync.
 *
 * Two display modes share the same data source (`useActiveSyncJob`):
 *   - <SyncProgressInline />  inline panel rendered on the BL status page
 *   - <SyncProgressBanner />  global, dismissable, mounted in AppLayout
 *
 * Data flow:
 *   1. Backend job.save() → RealtimeMixin emits `BookinglayerSyncJob` event.
 *   2. useRealtimeInvalidation invalidates ['bookinglayer','syncJobs'].
 *   3. useSyncJobs refetches; useActiveSyncJob exposes the running job.
 *   4. Polling at 3s acts as a fallback when the WebSocket is down.
 *
 * Banner dismiss is keyed by job id and held in an in-memory module variable
 * (browser storage is prohibited workspace-wide). The dismiss survives route
 * changes for the session but resets on a full reload. Once a new job starts
 * (new id), the banner re-appears even if the previous id was dismissed.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from '@/core';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, Loader2, X, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/theme/components/ui/progress';
import { Button } from '@/theme/components/ui/button';
import { useActiveSyncJob } from '../hooks/useBookinglayer';
import { useLocationUrl } from '@/shared/hooks/useLocationUrl';
import { useAuth } from '@/core/auth/useAuth';
import { canManageCompanyIntegrations } from '@/core/auth/modulePermissions';
import type { BookinglayerSyncJob, SyncJobPhase } from '../types';

const PHASE_ORDER: SyncJobPhase[] = [
  'queued',
  'syncing_accommodations',
  'syncing_guests',
  'syncing_bookings',
  'syncing_lines',
  'syncing_credit_notes',
  'syncing_refunds',
  'finalizing',
  'completed',
  'failed',
];

// Legacy-shape path; useLocationUrl() injects the active :locationId at render.
const STATUS_PAGE_PATH = '/location/settings/integrations/bookinglayer';

/** i18n label for a phase — falls back to a humanized phase string. */
function usePhaseLabel(phase: SyncJobPhase | undefined): string {
  const { t } = useTranslation();
  if (!phase) return '';
  const fallback = phase.replace(/_/g, ' ').replace(/^syncing\s+/, 'Syncing ');
  return t(`bl.sync.phase.${phase}`, fallback);
}

/**
 * Status copy used above the bar. "Syncing", "Sync complete", or
 * "Sync failed" depending on the job's terminal state.
 */
function useStatusLabel(job: BookinglayerSyncJob | null): string {
  const { t } = useTranslation();
  if (!job) return '';
  if (job.status === 'running') return t('bl.sync.status.running', 'Sync in progress');
  if (job.status === 'success') return t('bl.sync.status.success', 'Sync complete');
  if (job.status === 'partial') return t('bl.sync.status.partial', 'Sync completed with errors');
  return t('bl.sync.status.error', 'Sync failed');
}

interface BodyProps {
  job: BookinglayerSyncJob;
  variant: 'inline' | 'banner';
}

function SyncProgressBody({ job, variant }: BodyProps) {
  const { t } = useTranslation();
  const phaseLabel = usePhaseLabel(job.currentPhase);
  const statusLabel = useStatusLabel(job);

  const isTerminal = job.status !== 'running';
  const isError = job.status === 'error';

  const pct = Math.max(0, Math.min(100, job.progressPct ?? 0));
  const phaseIdx = PHASE_ORDER.indexOf(job.currentPhase) + 1;
  const phaseTotal = PHASE_ORDER.length - 2; // exclude completed/failed terminal pseudo-phases

  return (
    <div className={cn('flex w-full flex-col gap-2', variant === 'inline' && 'gap-3')}>
      <div className="flex items-center gap-2 text-sm">
        {isError ? (
          <XCircle className="h-4 w-4 text-destructive" />
        ) : isTerminal ? (
          <CheckCircle2 className="h-4 w-4 text-success" />
        ) : (
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
        )}
        <span className="font-medium text-foreground">{statusLabel}</span>
        {!isTerminal && phaseLabel && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">{phaseLabel}</span>
            {job.currentPhase !== 'queued' && (
              <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                {t('bl.sync.phaseStep', 'Step {{step}} / {{total}}', {
                  step: Math.min(phaseIdx, phaseTotal),
                  total: phaseTotal,
                })}
              </span>
            )}
          </>
        )}
        {!isTerminal && job.currentPhase === 'queued' && (
          <span className="ml-auto text-xs text-muted-foreground">
            {t('bl.sync.queued', 'Queued')}
          </span>
        )}
      </div>

      <Progress value={pct} className="h-2" />

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground tabular-nums">
        <span>
          {t('bl.sync.progressPct', '{{pct}}%', { pct })}
        </span>
        <span>
          {t('bl.sync.metric.fetched', 'Fetched {{n}}', { n: job.recordsFetched ?? 0 })}
        </span>
        <span>
          {t('bl.sync.metric.created', '+{{n}}', { n: job.recordsCreated ?? 0 })}
        </span>
        <span>
          {t('bl.sync.metric.updated', '~{{n}}', { n: job.recordsUpdated ?? 0 })}
        </span>
        {job.recordsTotal != null && (
          <span>
            {t('bl.sync.metric.total', 'of {{n}}', { n: job.recordsTotal })}
          </span>
        )}
      </div>

      {isError && job.errorMessage && (
        <p className="text-xs text-destructive line-clamp-2">{job.errorMessage}</p>
      )}
    </div>
  );
}

// ── Inline (rendered on the BL status page) ────────────────────────────────

// Auto-hide the inline panel this long after the job goes terminal.
// Without a timer the panel relied on `Date.now()` re-evaluating during
// a re-render — but nothing re-renders once polling stops, so a
// finished sync could sit on screen indefinitely. Longer than the
// banner's 4s: the operator is on the status page reading the counts.
const INLINE_HIDE_AFTER_TERMINAL_MS = 8_000;

export function SyncProgressInline() {
  const { job } = useActiveSyncJob();
  const [hiddenJobId, setHiddenJobId] = useState<string | null>(null);

  const isTerminal = !!job && job.status !== 'running';
  const jobId = job?.id ?? null;
  useEffect(() => {
    if (!jobId || !isTerminal) return;
    const timer = window.setTimeout(() => setHiddenJobId(jobId), INLINE_HIDE_AFTER_TERMINAL_MS);
    return () => window.clearTimeout(timer);
  }, [jobId, isTerminal]);

  if (!job || hiddenJobId === job.id) return null;
  return (
    <div className="rounded-lg border bg-card p-4">
      <SyncProgressBody job={job} variant="inline" />
    </div>
  );
}

// ── Banner (mounted globally in AppLayout) ─────────────────────────────────

// In-memory dismiss state. Browser storage is prohibited workspace-wide, so we
// hold the dismissed job id in a module-level variable. The banner is mounted
// once globally in AppLayout, so this survives route changes for the session
// (it resets on a full reload — acceptable for a transient sync banner). The
// trade-off vs the old localStorage approach: no cross-tab/reload persistence.
let dismissedJobIdMem: string | null = null;

function readDismissedJobId(): string | null {
  return dismissedJobIdMem;
}

function writeDismissedJobId(id: string | null): void {
  dismissedJobIdMem = id;
}

// Auto-dismiss the banner this many ms after the job reaches a
// terminal state. Long enough that the user reads "Sync complete",
// short enough that it doesn't linger.
const AUTO_DISMISS_AFTER_TERMINAL_MS = 4_000;

export function SyncProgressBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // The banner is mounted globally in AppLayout, but /sync/jobs requires
  // company.manage_integrations — a Frontdesk operator would 403 on every
  // poll. Only fetch when the user can actually manage integrations.
  const { job } = useActiveSyncJob({ enabled: canManageCompanyIntegrations(user) });
  const routeLocation = useLocation();
  const locUrl = useLocationUrl();
  const statusPath = locUrl(STATUS_PAGE_PATH);
  const [dismissedJobId, setDismissedJobId] = useState<string | null>(() => readDismissedJobId());

  // Auto-dismiss once the job lands in a terminal state. The hook
  // exposes the same job for ~60s after completion; we shorten that
  // tail to 4s for the banner so it doesn't sit around after the user
  // already saw "Sync complete". The job-id key in the dependency
  // list ensures a brand-new sync re-arms the timer cleanly.
  const isTerminal = !!job && job.status !== 'running';
  const jobId = job?.id ?? null;
  useEffect(() => {
    if (!jobId || !isTerminal) return;
    const timer = window.setTimeout(() => {
      writeDismissedJobId(jobId);
      setDismissedJobId(jobId);
    }, AUTO_DISMISS_AFTER_TERMINAL_MS);
    return () => window.clearTimeout(timer);
  }, [jobId, isTerminal]);

  // Hide on the BL status page itself — the inline version replaces it.
  if (routeLocation.pathname.startsWith(statusPath)) return null;

  if (!job) return null;
  if (dismissedJobId === job.id) return null;

  const handleDismiss = () => {
    writeDismissedJobId(job.id);
    setDismissedJobId(job.id);
  };

  return (
    <div
      className={cn(
        // Fixed footer: pinned to the bottom of the viewport, centered
        // horizontally, always 420px wide. On viewports narrower than
        // ~452px the inner padding still keeps the card readable.
        'fixed bottom-4 left-1/2 z-50 -translate-x-1/2',
        'w-[420px] max-w-[calc(100vw-2rem)]',
      )}
    >
      <div className="rounded-lg border bg-card shadow-lg p-4 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <SyncProgressBody job={job} variant="banner" />
          <div className="mt-3 flex items-center justify-end">
            <Button asChild variant="link" size="sm" className="h-auto p-0">
              <Link to={statusPath}>
                {t('bl.sync.openStatus', 'View details')}
              </Link>
            </Button>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={handleDismiss}
          aria-label={t('common.dismiss', 'Dismiss')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
